import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import quoteService, { type QuoteLookupContext } from '../../../services/quote.service';
import { formatWholeDollars } from '../../../utils/money';
import QuoteLookupField from '../../../components/QuoteLookupField';
import { useAdminOverride } from '../AdminOverrideContext';
import PermissionsContainer from '../../../components/PermissionsContainer';
import InvoiceOptionsButton from './InvoiceOptionsButton';
import {
  ACCESSORIAL_DELETE_COMPONENT_ID,
  ACCESSORIAL_DELETE_COMPONENT_NAME,
} from '../accessorialDeletePermission';
import DeleteAccessorialDialog from './DeleteAccessorialDialog';
import { getInvoiceLiTypes, type InvoiceLiType } from '../../../services/orderLookups.service';
import {
  invoiceLineItemsService,
  type InvoiceLineItemBatchItem,
} from '../../../services/invoiceLineItems.service';
import {
  accessorialsService,
  type AccessorialPayload,
} from '../../../services/accessorials.service';
import {
  computeLineTotal,
  glCodesForItem,
  glCodeFlagged,
  isPctRateType,
  RATE_TYPE_OPTIONS,
  type StagedChange,
} from './invoiceLineItemShared';

/**
 * Create/Edit modal for a single Accessorial (an additional cms.invoice row with
 * accessorialnum >= 1). FastCMS-styled port of V1's accessorial popup
 * (lib_editrecord.pl doaccessorial + doInvoicingLineItem). recno === null =>
 * create; a number edits that accessorial.
 *
 * Save is two steps (mirrors InvoicingView.saveChanges): the header create/update
 * hits /orders/{pro}/accessorials, then the line items are reconciled through the
 * existing accessorial-aware batch route (saveBatch(pro, rows, accessorialNum)).
 *
 * Portals to <body> at z-[130] above the Pro slide-over (z-[100]); a capture-phase
 * Escape handler closes THIS modal without closing the drawer.
 */

// ── V1-parity dropdown constants (hardcoded — do NOT fetch) ───────────────────
const BILL_FROM = ['CMS', 'DO NOT BILL'];
const DO_NOT_BILL_REASONS = [
  '', 'NOT APPROVED', 'RESEARCHING', 'OUT OF POCKET - COST OF DOING BUSINESS',
  'OUT OF POCKET - OFI', 'BILLED ELSEWHERE', 'DRY RUN', 'CHARITY', 'CUSTOMER PICKUP',
  'INVENTORY ADJUSTMENT', 'OVERFLOW IN RATE', 'OTHER',
];
const CREDIT_REASONS = [
  '', 'REBILLED: WRONG BILL TO', 'REBILLED: RATE DISCREPANCY',
  'REBILLED: INFORMATION/REF# DISCREPANCY', 'REBILLED: INCORRECT FORMATTING/GROUPING',
  'REBILLED: OTHER', 'NOT REBILLED: ACCOUNTING ERROR', 'NOT REBILLED: CSR ERROR',
  'NOT REBILLED: DISPATCH ERROR', 'NOT REBILLED: SALES ERROR', 'NOT REBILLED: OTHER',
  'WAIVING TO APPEASE CUSTOMER', 'WIRE TRANSFER FEE',
];
const REQUIRED_FLAG = ['N', 'Y', 'RESOLVED'];
const FULL_EMPTY = ['', 'FULL', 'EMPTY'];
const YESNO = ['N', 'Y'];
const MISSING_INV_REASONS = ['', 'BILLED ON ACCESSORIAL', 'INCORRECT INVOICE LINE ITEM', 'OTHER'];

// The 9 "Required For Invoicing" flags (label + payload key) + Full/Empty + Pending Approval.
const REQUIRED_FIELDS: { label: string; key: keyof AccessorialPayload }[] = [
  { label: 'Invoice Hold', key: 'requireRevisedDo' },
  { label: 'POD', key: 'requirePod' },
  { label: 'BOL', key: 'requireBol' },
  { label: 'MR/PL', key: 'requireMrpl' },
  { label: 'In Interchange', key: 'requireInInterchange' },
  { label: 'Out Interchange', key: 'requireOutInterchange' },
  { label: 'Chassis In EIR', key: 'requireChassisInEir' },
  { label: 'Chassis Out EIR', key: 'requireChassisOutEir' },
  { label: 'EDI', key: 'requireEdi' },
];

// ── compact input classes (no font-mono — project rule) ───────────────────────
const INPUT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-[3px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100 dark:disabled:bg-gray-800';
const SELECT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 py-[2px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-gray-800';
const TEXTAREA_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y disabled:bg-slate-100 dark:disabled:bg-gray-800';
const CELL_INPUT =
  'w-full bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 py-[2px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100 dark:disabled:bg-gray-800';

/**
 * Pure required-field validator (V1 parity-lite): Bill To is required, and a Do
 * Not Bill Status is required when Bill From is 'DO NOT BILL'. Credit amount is
 * intentionally NOT constrained (freely editable). Returns the first error or null.
 */
export function validateAccessorial(p: {
  billTo: number;
  billFromSystem: string;
  doNotBillReason: string;
}): string | null {
  if (!p.billTo || p.billTo <= 0) return 'Bill To is required';
  if ((p.billFromSystem || '').trim().toUpperCase() === 'DO NOT BILL'
      && !(p.doNotBillReason || '').trim()) {
    return 'Do Not Bill Status is required';
  }
  return null;
}

interface AccLineRow {
  recno?: number;
  lineNumber: number;
  item: string;
  glCode: string;
  qty: string;
  rate: string;
  rateType: string;
  description: string;
  _key: string;
}

/** ISO 'YYYY-MM-DD' -> 'MM/DD/YYYY' for the read-only date rows; '' when unset. */
const displayDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m && m[1] !== '0000' ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

let _rowKey = 0;
const emptyRow = (): AccLineRow => ({
  lineNumber: 0, item: '', glCode: '', qty: '', rate: '', rateType: '',
  description: '', _key: `accli-${++_rowKey}`,
});

/**
 * Fold the Invoice Estimate's proposed changes into this document's rows.
 *
 * The twin of InvoicingView's `applyEstimateToRows`; both take their list from the
 * shared `estimateStagedChanges`, so WHICH lines stage and with what figures is decided
 * in one place and only the row shape differs. Here that shape is the form's: qty and
 * rate are strings because they are bound to text inputs, and there is no `total`
 * column to set — it is computed from the other three on every render.
 *
 * A staged 0 rate is written as '' rather than '0'. The estimate sends 0 for a figure it
 * projected from an assumed date and deliberately will not fill in (`applyRate: false`),
 * and an empty box reads as "needs a number" where a typed 0 reads as "the rate is zero".
 */
export function applyStagedChanges(
  rows: ReadonlyArray<AccLineRow>,
  changes: ReadonlyArray<StagedChange> | null | undefined,
): { rows: AccLineRow[]; stagedKeys: Set<string> } {
  const stagedKeys = new Set<string>();
  if (!changes || changes.length === 0) return { rows: [...rows], stagedKeys };

  let next = [...rows];
  for (const change of changes) {
    if (change.action === 'remove') {
      next = next.filter((r) => r.recno !== change.existingRecno);
      continue;
    }
    const num = (v: number) => (v ? String(v) : '');
    if (change.action === 'update') {
      next = next.map((r) => {
        if (r.recno !== change.existingRecno) return r;
        stagedKeys.add(r._key);
        return {
          ...r,
          qty: num(change.qty),
          rate: num(change.rate),
          rateType: change.rateType || r.rateType,
          description: change.description,
          glCode: change.glCode || r.glCode,
        };
      });
      continue;
    }
    const _key = `accli-${++_rowKey}`;
    stagedKeys.add(_key);
    next.push({
      lineNumber: next.length,
      item: change.item,
      glCode: change.glCode,
      qty: num(change.qty),
      rate: num(change.rate),
      rateType: change.rateType,
      description: change.description,
      _key,
    });
  }
  return { rows: next, stagedKeys };
}

interface Props {
  open: boolean;
  // number for a live order; "T…" string for a template (endpoints are template-aware).
  pronumber: number | string;
  recno: number | null; // null => create; number => edit
  onClose: () => void;
  onSaved: () => void;
  /** Open form's quote-match context, forwarded from the Invoicing tab so this
   *  modal's spyglass matches STAGED order values too. The order-level inputs
   *  (rate zone / bill-to / delivery leg) are shared with the parent order, and
   *  V1's accessorial spyglass read them off the very same form. */
  quoteLookupContext?: QuoteLookupContext | null;
  /** Changes the Invoice Estimate proposes for THIS accessorial, staged into the line
   *  grid once it loads. Unsaved — the user reviews them and saves, exactly as "Stage
   *  in form" works for the main invoice. The estimate cannot write them itself: the
   *  Invoicing tab's form holds the MAIN invoice's lines, so an accessorial's proposal
   *  had nowhere to land and the modal showed it as display-only. */
  stagedChanges?: ReadonlyArray<StagedChange> | null;
  /** The order's masterpro — offers Invoice Estimate Options on a saved, undated accessorial. */
  masterPro?: string;
}

/** Right-aligned bold label + field, one row. */
const Row: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label, required, children,
}) => (
  <div className="flex items-center gap-2 py-0.5">
    <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
  </div>
);

/**
 * Gate for the Delete Accessorial button. `defaultHidden`, so no grant means no
 * button — and PermissionsContainer, not a hook, because only the container mounts
 * PermissionConfigurable, which registers the component with the in-app permissions
 * editor while Permissions Mode is on. Gating with a hook resolves the level
 * correctly but leaves the component editable only by hand in `component_permissions`.
 *
 * `mr-auto` is passed here AND set on the button: outside permissions mode
 * PermissionConfigurable renders its children bare (no wrapper div), so the button is
 * the flex child; inside it, the frame div is, and it needs the margin instead.
 */
const AccessorialDeleteGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PermissionsContainer
    componentId={ACCESSORIAL_DELETE_COMPONENT_ID}
    componentName={ACCESSORIAL_DELETE_COMPONENT_NAME}
    defaultHidden
    className="mr-auto"
  >
    {children}
  </PermissionsContainer>
);

const AccessorialEditModal: React.FC<Props> = ({
  open, pronumber, recno, onClose, onSaved, quoteLookupContext, stagedChanges, masterPro = '',
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isEdit = recno != null;

  // Header state.
  const [accessorialNum, setAccessorialNum] = useState<number | null>(null);
  // recno of a row this modal created in create mode, so a retry after a half-failed
  // save updates it instead of creating another. Cleared by resetToCreate.
  const [createdRecno, setCreatedRecno] = useState<number | null>(null);
  // invoice_history.masterpro — blank until this accessorial has been invoiced.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  // The raised invoice itself. Each accessorial is invoiced on its own, so this is the
  // accessorial's invoice — not the pro's main one. 0 until it is invoiced, which is
  // what hides Invoice Options.
  const [invHistoryRecno, setInvHistoryRecno] = useState(0);
  const [billToRecno, setBillToRecno] = useState(0);
  const [billToName, setBillToName] = useState('');
  const [billFromSystem, setBillFromSystem] = useState('CMS');
  const [doNotBillReason, setDoNotBillReason] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [masExportDate, setMasExportDate] = useState('');
  const [billToRefNo, setBillToRefNo] = useState('');
  const [invoiceComments, setInvoiceComments] = useState('');
  const [quoteNumber, setQuoteNumber] = useState('');
  const [revisionNumber, setRevisionNumber] = useState('');
  const [v2QuoteNumber, setV2QuoteNumber] = useState('');
  const [v2Lane, setV2Lane] = useState('');
  const [v2Revision, setV2Revision] = useState('');
  const [flags, setFlags] = useState<Record<string, string>>({});
  const [invFullEmpty, setInvFullEmpty] = useState('');
  const [pendingApproval, setPendingApproval] = useState('Y');
  const [missingInvApp, setMissingInvApp] = useState('N');
  const [missingInvReason, setMissingInvReason] = useState('');
  const [invoiced, setInvoiced] = useState(false);
  const [rcvWhseList, setRcvWhseList] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [createdAtDisplay, setCreatedAtDisplay] = useState('');

  const [lineRows, setLineRows] = useState<AccLineRow[]>([]);
  //: Rows the Invoice Estimate staged on open — highlighted so a change the user did not
  //: type is visibly not theirs, and visibly not saved yet. Same blue the Invoicing tab
  //: uses for the same thing.
  const [stagedKeys, setStagedKeys] = useState<Set<string>>(() => new Set());
  const [liTypes, setLiTypes] = useState<InvoiceLiType[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bill To typeahead (debounced billto lookup → stores billtos.recno).
  const [billToOptions, setBillToOptions] = useState<TypeAheadOption[]>([]);
  const [billToSearching, setBillToSearching] = useState(false);
  const billToTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const billToSeq = useRef(0);

  // V1 parity: once the accessorial is invoiced, V1's `orderinvoiced` ACL locks Bill To,
  // Bill From, Invoice Comments and the line items — and nothing else. Every other header
  // field (Bill To Ref first among them: a reference the customer needs on an invoice
  // that has already gone out) stays editable, so `ro` is applied to exactly that set,
  // and the backend refuses a change to the same set (accessorials.py
  // _INVOICED_LOCKED_COLS). Admin Override (#33501) releases the lock — same record-state
  // lock as the main invoice's line grid, and the correction still runs down the
  // ordinary delta-writer so history and every downstream effect land as on a normal
  // edit.
  //
  // `invoiced` stays the FLOOR rather than being replaced by a config lookup: the lock
  // must not depend on anything that can fail to load. Override only ever releases it.
  const { active: adminOverrideActive } = useAdminOverride();
  const ro = invoiced && !adminOverrideActive;

  // Capture-phase Escape. It listens on `document`, so it also sees keys pressed in
  // the delete dialog that portals out of this subtree — while that is open it owns
  // Escape, and backing out of it must not take the editor with it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (deleteOpen) { setDeleteOpen(false); return; }
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
    // `deleteOpen` is read inside the handler — without it here the listener keeps
    // the value from the render that installed it and never sees the dialog open.
  }, [open, onClose, deleteOpen]);

  // Item ("liitem") options.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    getInvoiceLiTypes()
      .then((r) => { if (alive) setLiTypes(r); })
      .catch(() => { /* leave empty; stored item still shows */ });
    return () => { alive = false; };
  }, [open]);

  /** The spyglass answers for THIS accessorial, not for the order.
   *
   *  `billto` is a column on the INVOICE row, and an accessorial routinely carries a
   *  different one — it is often billed to another party entirely. The context handed
   *  down is the order form's, whose bill-to belongs to the main invoice, so without
   *  this the lookup matched quotes against the wrong customer on exactly the
   *  documents most likely to need their own.
   *
   *  `accessorialnum` does the same for the SAVED fallback, so a lookup still lands on
   *  the right document when the form has no bill-to to override with. Both are sent
   *  only when they hold something: a blank reads as "the form says blank" server-side
   *  and would override the saved value rather than defer to it, and in create mode
   *  there is no accessorial on the server to read.
   *
   *  Everything else in the context (rate zone, move type, delivery leg) is genuinely
   *  order-level and shared, which is why it passes through untouched. */
  const accessorialQuoteContext = useMemo(() => {
    const scope = {
      ...(billToRecno > 0 ? { billto: billToRecno } : {}),
      ...(accessorialNum ? { accessorialnum: accessorialNum } : {}),
    };
    if (!quoteLookupContext) return Object.keys(scope).length ? scope : null;
    return { ...quoteLookupContext, ...scope };
  }, [quoteLookupContext, billToRecno, accessorialNum]);

  const liTypeOptions = useMemo<TypeAheadOption[]>(
    () => liTypes.map((t) => ({
      value: t.name,
      label: t.name,
      description: t.displayName && t.displayName !== t.name ? t.displayName : t.glType,
      data: t,
    })),
    [liTypes],
  );
  const glTypeByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of liTypes) m.set(t.name, t.glType);
    return m;
  }, [liTypes]);

  const resetToCreate = () => {
    setAccessorialNum(null);
    setCreatedRecno(null);
    setInvoiceNumber('');
    setInvHistoryRecno(0);
    setBillToRecno(0); setBillToName('');
    setBillFromSystem('CMS'); setDoNotBillReason('');
    setCreditAmount(''); setCreditReason('');
    setInvoiceDate(''); setMasExportDate('');
    setBillToRefNo(''); setInvoiceComments('');
    setQuoteNumber(''); setRevisionNumber('');
    setV2QuoteNumber(''); setV2Lane(''); setV2Revision('');
    setFlags({});
    setInvFullEmpty(''); setPendingApproval('Y');
    setMissingInvApp('N'); setMissingInvReason('');
    setInvoiced(false); setCreatedBy(''); setCreatedAtDisplay('');
    setLineRows([]);
    setStagedKeys(new Set());
  };

  // Load on open (create resets to blanks + pulls rcvWhseList from the list;
  // edit hydrates the header and its line items).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setBillToOptions([]);

    if (!isEdit) {
      resetToCreate();
      setLoading(false);
      accessorialsService.list(pronumber)
        .then((res) => { if (!cancelled) setRcvWhseList(res.rcvWhseList || ''); })
        .catch(() => { if (!cancelled) setRcvWhseList(''); });
      return () => { cancelled = true; };
    }

    setLoading(true);
    accessorialsService.get(pronumber, recno)
      .then(async (rec) => {
        if (cancelled) return;
        setAccessorialNum(rec.accessorialNum);
        setInvoiceNumber(String(rec.invoiceNumber ?? '').trim());
        setInvHistoryRecno(Number(rec.invHistoryRecno ?? 0));
        setBillToRecno(rec.billTo); setBillToName(rec.billToName);
        setBillFromSystem(rec.billFromSystem || 'CMS');
        setDoNotBillReason(rec.doNotBillReason);
        setCreditAmount(rec.creditAmount ? String(rec.creditAmount) : '');
        setCreditReason(rec.creditReason);
        setInvoiceDate(rec.invoiceDate); setMasExportDate(rec.masExportDate);
        setBillToRefNo(rec.billToRefNo); setInvoiceComments(rec.invoiceComments);
        setQuoteNumber(rec.quoteNumber ? String(rec.quoteNumber) : '');
        setRevisionNumber(rec.revisionNumber ? String(rec.revisionNumber) : '');
        setV2QuoteNumber(rec.v2QuoteNumber ? String(rec.v2QuoteNumber) : '');
        setV2Lane(rec.v2Lane);
        setV2Revision(rec.v2Revision ? String(rec.v2Revision) : '');
        setFlags({
          requireRevisedDo: rec.requireRevisedDo, requirePod: rec.requirePod,
          requireBol: rec.requireBol, requireMrpl: rec.requireMrpl,
          requireInInterchange: rec.requireInInterchange,
          requireOutInterchange: rec.requireOutInterchange,
          requireChassisInEir: rec.requireChassisInEir,
          requireChassisOutEir: rec.requireChassisOutEir,
          requireEdi: rec.requireEdi,
        });
        setInvFullEmpty(rec.invFullEmpty);
        setPendingApproval(rec.pendingApproval || 'N');
        setMissingInvApp(rec.missingInvApp || 'N');
        setMissingInvReason(rec.missingInvReason || '');
        setInvoiced(!!rec.invoiced);
        setRcvWhseList(rec.rcvWhseList || '');
        setCreatedBy(rec.createdBy); setCreatedAtDisplay(rec.createdAtDisplay);

        const items = await invoiceLineItemsService.list(pronumber, rec.accessorialNum);
        if (cancelled) return;
        const loaded: AccLineRow[] = items.map((li) => ({
          recno: li.recno,
          lineNumber: li.lineNumber,
          item: li.item || '',
          glCode: li.glCode || '',
          qty: li.qty ? String(li.qty) : '',
          rate: li.rate ? String(li.rate) : '',
          rateType: li.rateType || '',
          description: li.description || '',
          _key: `accli-${++_rowKey}`,
        }));
        // Stage the estimate's proposal for this document, if the Invoice Estimate sent
        // one. After the load, never instead of it: the user is reviewing a change
        // against what the invoice bills today, so the untouched lines have to be there.
        const { rows: staged, stagedKeys: keys } = applyStagedChanges(loaded, stagedChanges);
        setLineRows(staged);
        setStagedKeys(keys);
        setLoadedTick((t) => t + 1);
      })
      .catch(() => { if (!cancelled) setError('Failed to load accessorial.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, pronumber, recno, isEdit]);

  // Debounced bill-to lookup (min 2 chars, ~300ms) — stores billtos.recno.
  const searchBillTo = (term: string) => {
    if (billToTimer.current) clearTimeout(billToTimer.current);
    const q = term.trim();
    // A linked bill-to suppresses search (the input still carries the "#id"
    // suffix on the first keystroke); that keystroke unlinks via onChange,
    // re-enabling search on the next one — same pattern as the quote wizard.
    if (q.length < 2 || billToRecno > 0) { setBillToOptions([]); setBillToSearching(false); return; }
    // In flight from the FIRST keystroke (debounce included) so the dropdown shows
    // "Searching…" instead of a premature "No results found"; the sequence counter
    // drops out-of-order responses from stale keystrokes (LookupSearchField pattern).
    const seq = ++billToSeq.current;
    setBillToSearching(true);
    billToTimer.current = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupBillTo(q);
        if (seq !== billToSeq.current) return;
        setBillToOptions(rows.map((r) => ({
          value: String(r.billto),
          label: r.billtoname,
          labelSuffix: `#${r.billto}`,
          description: [
            [r.city, r.state].filter(Boolean).join(', '),
            // Sage tag with its rounded AR balance, e.g. "MAERSK2 - $340,592".
            [r.mas90code, r.arBalance !== '' ? formatWholeDollars(r.arBalance) : '']
              .filter(Boolean).join(' - '),
          ].filter(Boolean).join('  ·  '),
          data: r,
        })));
      } catch { if (seq === billToSeq.current) setBillToOptions([]); }
      finally { if (seq === billToSeq.current) setBillToSearching(false); }
    }, 300);
  };

  const setFlag = (key: string, val: string) => setFlags((f) => ({ ...f, [key]: val }));

  const addLineItem = () => setLineRows((prev) => [...prev, emptyRow()]);
  const updateLineItem = (index: number, patch: Partial<AccLineRow>) =>
    setLineRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  // The batch reconcile removes rows by ABSENCE (soft-remove), so deleting simply
  // drops the row from the keep-set — no per-row deleted flag needed.
  const removeLineItem = (index: number) =>
    setLineRows((prev) => prev.filter((_, i) => i !== index));

  const showCreditReason = !!creditAmount && creditAmount.trim() !== '' && Number(creditAmount) !== 0;
  const showDoNotBill = billFromSystem === 'DO NOT BILL';
  const showMissingReason = missingInvApp === 'Y';

  const buildPayload = (): AccessorialPayload => ({
    billTo: billToRecno,
    billFromSystem,
    doNotBillReason: showDoNotBill ? doNotBillReason : '',
    creditAmount,
    creditReason: showCreditReason ? creditReason : '',
    // invoiceDate / masExportDate are deliberately absent — accounting owns both and
    // the server ignores them on this route. See the read-only rows below.
    billToRefNo,
    invoiceComments,
    quoteNumber,
    revisionNumber,
    v2QuoteNumber,
    v2Revision,
    v2Lane,
    requireRevisedDo: flags.requireRevisedDo || 'N',
    requirePod: flags.requirePod || 'N',
    requireBol: flags.requireBol || 'N',
    requireMrpl: flags.requireMrpl || 'N',
    requireInInterchange: flags.requireInInterchange || 'N',
    requireOutInterchange: flags.requireOutInterchange || 'N',
    requireChassisInEir: flags.requireChassisInEir || 'N',
    requireChassisOutEir: flags.requireChassisOutEir || 'N',
    requireEdi: flags.requireEdi || 'N',
    invFullEmpty,
    pendingApproval: pendingApproval || 'N',
    missingInvApp,
    missingInvReason: showMissingReason ? missingInvReason : '',
  });

  const toBatch = (): InvoiceLineItemBatchItem[] =>
    lineRows
      .filter((r) => r.recno != null || r.item.trim() !== '')
      .map((r) => {
        const qty = Number(r.qty) || 0;
        const rate = Number(r.rate) || 0;
        return {
          recno: r.recno ?? null,
          lineNumber: r.lineNumber,
          item: r.item,
          glCode: r.glCode,
          qty,
          rate,
          rateType: r.rateType,
          description: r.description,
          total: computeLineTotal(qty, rate, r.rateType),
        };
      });

  // What the form held once the record loaded. Invoice Estimate Options renders the SAVED
  // accessorial, so it waits while anything differs — or while a staged proposal is in
  // the grid, which the snapshot includes because staging happens during the load.
  const [loadedTick, setLoadedTick] = useState(0);
  // State, not a ref: taking the snapshot has to re-render, or the button would stay
  // disabled after a load until something else happened to redraw it.
  const [loadedSnapshot, setLoadedSnapshot] = useState<string | null>(null);
  const formSnapshot = () => JSON.stringify([buildPayload(), toBatch()]);
  useEffect(() => {
    if (loadedTick) setLoadedSnapshot(formSnapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedTick]);
  const unsavedForEstimate = stagedKeys.size > 0
    || loadedSnapshot === null
    || formSnapshot() !== loadedSnapshot;

  const handleSave = async () => {
    const msg = validateAccessorial({ billTo: billToRecno, billFromSystem, doNotBillReason });
    if (msg) { setError(msg); return; }
    // Same rule as the main Invoicing grid: a blank `liitem` is V1's soft-remove marker,
    // so saving one leaves a row that bills real money under no charge name and that no
    // read path will ever show again. `toBatch` already drops an untouched new row, so
    // what reaches here is an EXISTING line whose Item was cleared — the delete that
    // wants the ✕ button.
    //
    // Checked here rather than left to the backend's 400: on a create the accessorial
    // header is already written by the time the line-item batch runs, and the catch
    // below reports every failure as one generic string, so the user would be left with
    // an empty accessorial and nothing explaining it.
    const blankIdx = lineRows.findIndex((r) => r.recno != null && !r.item.trim());
    if (blankIdx >= 0) {
      setError(
        `Line ${blankIdx + 1} has no Item. Pick an Item, or remove the line with the ` +
        '✕ button — clearing the Item does not delete it.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      // The header and its line items are two requests, so a create whose line-item
      // batch then fails leaves a real accessorial behind. Remember its recno: a retry
      // must UPDATE that row, never mint a second one. (Four empty accessorials on
      // 5093978, three on 5054676 — all from retries after a line-item 409.)
      const existing = recno ?? createdRecno;
      const res = existing != null
        ? await accessorialsService.update(pronumber, existing, payload, adminOverrideActive)
        : await accessorialsService.create(pronumber, payload);
      if (existing == null) { setCreatedRecno(res.recno); setAccessorialNum(res.accessorialNum); }
      // The header above already carried Pending Approval, so tell the line-item save
      // that this same action set it. Without that the batch's re-flag sent a freshly
      // RESOLVED accessorial straight back to Pending Approval — the user was doing
      // both things deliberately, and the header write lost because it went first.
      // The server re-checks the value against the lines as saved, so this asks for
      // nothing it has not earned.
      //
      // Skipped while the invoiced lock holds: the line grid was disabled, so the
      // batch would carry no change, and the line-item route refuses an invoiced
      // invoice with a 409 that would fail the header save that just succeeded.
      if (!ro) {
        await invoiceLineItemsService.saveBatch(
          pronumber, toBatch(), res.accessorialNum, adminOverrideActive,
          payload.pendingApproval,
        );
      }
      onSaved();
      onClose();
    } catch (e) {
      // Prefer the server's own reason. The generic string was fine while every failure
      // here was a 409 the user could do nothing about; the line-item routes now reject
      // for reasons that name the offending line and say how to fix it, and swallowing
      // those leaves the user retrying the same save.
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? detail : 'Failed to save accessorial.');
    } finally {
      setSaving(false);
    }
  };

  // Alt+S saves, Alt+C closes — mirroring the footer buttons, including the
  // in-flight guard. Save stays available on an invoiced accessorial: the unlocked
  // header fields still need a way to be written. The layer keeps both off the Pro
  // drawer's own Save behind this modal.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: open },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  // The invoice number only exists once the accessorial is invoiced, so it joins the
  // title rather than holding a slot in it — an un-invoiced accessorial reads exactly
  // as it did before.
  const title = isEdit
    ? `Accessorial ${accessorialNum ?? ''} — Pro ${pronumber}${
        invoiceNumber ? ` — Invoice #: ${invoiceNumber}` : ''}`
    : `New Accessorial — Pro ${pronumber}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      data-testid="accessorial-edit-modal"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          closeLabel="Close accessorial editor"
          dragHandleProps={dragHandleProps}
        />

        {/* Body */}
        <div className="overflow-y-auto px-4 py-3 text-[0.78125rem] text-slate-700 dark:text-gray-200">
          {loading ? (
            <p className="py-6 text-center text-slate-400 dark:text-gray-500">Loading…</p>
          ) : (
            <>
              {ro && (
                <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[0.75rem] font-semibold text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  This accessorial has been invoiced — Bill To, Bill From, Invoice Comments
                  and the line items are locked. Other fields can still be saved.
                </p>
              )}

              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                {/* Left column */}
                <div>
                  <Row label="Bill To" required>
                    <TypeAhead
                      freeText compact multiple={false} showDescriptions
                      value={billToName}
                      // Linked bill-to shows its recno as the gray in-field
                      // adornment (display only — the payload uses billToRecno).
                      valueSuffix={billToRecno > 0 ? `#${billToRecno}` : undefined}
                      options={billToOptions}
                      disabled={ro}
                      onChange={(v) => { setBillToName(v); setBillToRecno(0); }}
                      onSearch={searchBillTo}
                      onSelect={(opt) => {
                        const r = opt.data as { billto?: number; billtoname?: string };
                        setBillToRecno(Number(r.billto) || 0);
                        setBillToName(r.billtoname || '');
                      }}
                      ariaLabel="Bill To"
                      placeholder="Search by name, Sage Tag, or #…"
                      className="w-full"
                      searching={billToSearching}
                      emptyMessage={billToName.trim().length < 2
                        ? 'Type at least 2 characters to search'
                        : undefined}
                    />
                  </Row>
                  <Row label="Bill To Ref #">
                    <input
                      type="text" className={INPUT_CLS} aria-label="Bill To Ref #"
                      value={billToRefNo}
                      onChange={(e) => setBillToRefNo(e.target.value.toUpperCase())}
                    />
                  </Row>
                  <Row label="Bill From">
                    <select
                      className={SELECT_CLS} aria-label="Bill From" disabled={ro}
                      value={billFromSystem}
                      onChange={(e) => setBillFromSystem(e.target.value)}
                    >
                      {BILL_FROM.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Row>
                  {showDoNotBill && (
                    <Row label="Do Not Bill Status" required>
                      <select
                        className={SELECT_CLS} aria-label="Do Not Bill Status" disabled={ro}
                        value={doNotBillReason}
                        onChange={(e) => setDoNotBillReason(e.target.value)}
                      >
                        {DO_NOT_BILL_REASONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                      </select>
                    </Row>
                  )}
                  {/* Both dates are READ-ONLY here, exactly as on the main invoice's
                      Invoicing tab. Accounting owns them: invoicedate is stamped by
                      invoice creation, masexportdate by the Sage export. They were
                      editable until 2026-10, and a CSR filling them on a NEW accessorial
                      made it born-invoiced — which locks the line-item batch (409) and
                      leaves an empty accessorial nobody can finish. */}
                  <Row label="Invoice Date">
                    <span
                      aria-label="Invoice Date"
                      className="text-[0.75rem] text-slate-700 dark:text-gray-200"
                    >
                      {displayDate(invoiceDate) || '—'}
                    </span>
                  </Row>
                  <Row label="Sage Date">
                    <span
                      aria-label="Sage Date"
                      className="text-[0.75rem] text-slate-700 dark:text-gray-200"
                    >
                      {displayDate(masExportDate) || '—'}
                    </span>
                  </Row>
                  <Row label="Credit Amt">
                    <input
                      type="number" className={INPUT_CLS} aria-label="Credit Amt"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                    />
                  </Row>
                  {showCreditReason && (
                    <Row label="Credit Reason">
                      <select
                        className={SELECT_CLS} aria-label="Credit Reason"
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                      >
                        {CREDIT_REASONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                      </select>
                    </Row>
                  )}
                </div>

                {/* Right column */}
                <div>
                  <Row label="Missing Inv App">
                    <select
                      className={SELECT_CLS} aria-label="Missing Inv App"
                      value={missingInvApp}
                      onChange={(e) => setMissingInvApp(e.target.value)}
                    >
                      {YESNO.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Row>
                  {showMissingReason && (
                    <Row label="Missing Inv Reason">
                      <select
                        className={SELECT_CLS} aria-label="Missing Inv Reason"
                        value={missingInvReason}
                        onChange={(e) => setMissingInvReason(e.target.value)}
                      >
                        {MISSING_INV_REASONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                      </select>
                    </Row>
                  )}
                  <Row label="Quote # / Rev #">
                    {/* One picker for the pair (V1 lib_editrecord.pl:9082-9152): a
                        typeahead over quote numbers plus a spyglass that suggests the
                        quote matching this PRO. Picking sets both halves at once, so
                        they can't drift apart. */}
                    <QuoteLookupField
                      quoteNumber={parseInt(quoteNumber, 10) || 0}
                      revisionNumber={parseInt(revisionNumber, 10) || 0}
                      onChange={(qn, rn) => {
                        setQuoteNumber(qn ? String(qn) : '');
                        setRevisionNumber(rn ? String(rn) : '');
                      }}
                      pronumber={pronumber}
                      context={accessorialQuoteContext}
                      className="flex-1"
                    />
                  </Row>
                  <Row label="V2 Quote/Lane/Rev">
                    <input
                      type="text" className={INPUT_CLS} aria-label="V2 Quote Number"
                      value={v2QuoteNumber} onChange={(e) => setV2QuoteNumber(e.target.value)}
                    />
                    <input
                      type="text" className={INPUT_CLS} aria-label="V2 Lane"
                      value={v2Lane} onChange={(e) => setV2Lane(e.target.value.toUpperCase())}
                    />
                    <input
                      type="text" className={INPUT_CLS} aria-label="V2 Revision"
                      value={v2Revision} onChange={(e) => setV2Revision(e.target.value)}
                    />
                  </Row>
                  <Row label="Invoice Comments">
                    <textarea
                      className={TEXTAREA_CLS} aria-label="Invoice Comments" rows={3} disabled={ro}
                      value={invoiceComments}
                      onChange={(e) => setInvoiceComments(e.target.value)}
                    />
                  </Row>
                  {createdBy && (
                    <Row label="Created by">
                      <span className="text-[0.75rem] text-slate-500 dark:text-gray-400">
                        {createdBy}{createdAtDisplay ? ` · ${createdAtDisplay}` : ''}
                      </span>
                    </Row>
                  )}
                </div>
              </div>

              {/* Required For Invoicing */}
              <div className="mt-3 border-t border-slate-200 pt-2 dark:border-gray-700">
                <h3 className="mb-1 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">
                  Required For Invoicing
                </h3>
                <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                  {REQUIRED_FIELDS.map((f) => {
                    const val = flags[f.key as string] || 'N';
                    return (
                      <div
                        key={f.key as string}
                        className={`flex items-center gap-2 py-0.5 ${val === 'Y' ? 'bg-yellow-100 dark:bg-yellow-900/30 rounded' : ''}`}
                      >
                        <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
                          {f.label}
                        </label>
                        <select
                          className={SELECT_CLS} aria-label={f.label}
                          value={val}
                          onChange={(e) => setFlag(f.key as string, e.target.value)}
                        >
                          {REQUIRED_FLAG.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 py-0.5">
                    <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
                      Full/Empty
                    </label>
                    <select
                      className={SELECT_CLS} aria-label="Full/Empty"
                      value={invFullEmpty} onChange={(e) => setInvFullEmpty(e.target.value)}
                    >
                      {FULL_EMPTY.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                    </select>
                  </div>
                  <div className={`flex items-center gap-2 py-0.5 ${pendingApproval === 'Y' ? 'bg-yellow-100 dark:bg-yellow-900/30 rounded' : ''}`}>
                    <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
                      Pending Approval
                    </label>
                    <select
                      className={SELECT_CLS} aria-label="Pending Approval"
                      value={pendingApproval} onChange={(e) => setPendingApproval(e.target.value)}
                    >
                      {REQUIRED_FLAG.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div className="mt-3 border-t border-slate-200 pt-2 dark:border-gray-700">
                {/* Staged, not saved. Said plainly because the highlighted figures did
                    not come from this user and nothing is written until they Save. */}
                {stagedKeys.size > 0 && (
                  <div className="mb-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-[0.75rem] text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    The highlighted {stagedKeys.size === 1 ? 'line is' : 'lines are'} staged
                    from Invoice Reconciliation and {stagedKeys.size === 1 ? 'has' : 'have'} not
                    been saved. Review {stagedKeys.size === 1 ? 'it' : 'them'}, then Save to
                    apply.
                  </div>
                )}
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">
                      Line Items
                    </h3>
                    {/* Beside the line items, where V1 puts it on the accessorial's own
                        invoice screen. An accessorial is its OWN invoice with its own
                        number and PDF, so it gets its own options rather than sharing
                        the main invoice's. Above this modal's z-[130]. */}
                    <InvoiceOptionsButton
                      invHistoryRecno={invHistoryRecno}
                      billto={billToRecno}
                      invoiceDate={invoiceDate}
                      invoiceNumber={invoiceNumber}
                      title={`Accessorial ${accessorialNum ?? ''} — Invoice ${invoiceNumber}`.trim()}
                      layerClassName="z-[140]"
                      masterpro={isEdit ? masterPro : ''}
                      unsaved={unsavedForEstimate}
                    />
                  </div>
                  <button
                    type="button" onClick={addLineItem} disabled={ro} aria-label="Add line item"
                    className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
                {lineRows.length === 0 ? (
                  <p className="py-2 text-center text-[0.71875rem] text-slate-400 dark:text-gray-500">
                    No line items
                  </p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        <th className="py-1 w-8">#</th>
                        <th className="py-1">Item</th>
                        <th className="py-1 w-28">GL Code</th>
                        <th className="py-1 w-14">Qty</th>
                        <th className="py-1 w-16">Rate</th>
                        <th className="py-1 w-20">Type</th>
                        <th className="py-1">Description</th>
                        <th className="py-1 w-20 text-right">Total</th>
                        <th className="py-1 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {lineRows.map((r, index) => {
                        const glOptions = glCodesForItem(r.item);
                        const flagged = glCodeFlagged(r.glCode, rcvWhseList);
                        const total = computeLineTotal(Number(r.qty) || 0, Number(r.rate) || 0, r.rateType);
                        return (
                          <tr
                            key={r._key}
                            className={`border-t border-slate-100 dark:border-gray-700/60 align-top${
                              stagedKeys.has(r._key) ? ' bg-blue-200 dark:bg-blue-900/40' : ''
                            }`}
                          >
                            <td className="py-1 pr-1 text-[0.71875rem] text-slate-500 dark:text-gray-400">{index + 1}</td>
                            <td className="py-1 pr-1">
                              <TypeAhead
                                freeText compact multiple={false} showDescriptions
                                value={r.item}
                                options={liTypeOptions}
                                disabled={ro}
                                onChange={(v) => updateLineItem(index, { item: v })}
                                onSelect={(opt) => updateLineItem(index, {
                                  item: opt.value,
                                  glCode: glTypeByName.get(opt.value) || r.glCode,
                                })}
                                ariaLabel="Item"
                                className="w-full"
                              />
                            </td>
                            <td className="py-1 pr-1">
                              <select
                                className={`${CELL_INPUT} ${flagged ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' : ''}`}
                                aria-label="GL Code" disabled={ro}
                                value={r.glCode}
                                onChange={(e) => updateLineItem(index, { glCode: e.target.value })}
                              >
                                <option value="" />
                                {/* keep a legacy/out-of-list value visible */}
                                {r.glCode && !glOptions.includes(r.glCode) && (
                                  <option value={r.glCode}>{r.glCode}</option>
                                )}
                                {glOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                            <td className="py-1 pr-1">
                              <input
                                type="number" className={CELL_INPUT} aria-label="Qty" disabled={ro}
                                value={r.qty}
                                onChange={(e) => updateLineItem(index, { qty: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-1">
                              <input
                                type="number" className={CELL_INPUT} aria-label="Rate" disabled={ro}
                                value={r.rate}
                                onChange={(e) => updateLineItem(index, { rate: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-1">
                              <select
                                className={CELL_INPUT} aria-label="Type" disabled={ro}
                                value={r.rateType}
                                onChange={(e) => updateLineItem(index, { rateType: e.target.value })}
                              >
                                <option value="" />
                                {r.rateType && !RATE_TYPE_OPTIONS.includes(r.rateType) && (
                                  <option value={r.rateType}>{r.rateType}</option>
                                )}
                                {RATE_TYPE_OPTIONS.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                              </select>
                            </td>
                            <td className="py-1 pr-1">
                              <input
                                type="text" className={CELL_INPUT} aria-label="Description" disabled={ro}
                                value={r.description}
                                // NOT upper-cased, matching the main invoice's own
                                // description box (InvoicingView). The modal forces case
                                // on billToRefNo and v2Lane, which are reference codes
                                // where that is the convention; the description is free
                                // text and was swept along with them. Nothing downstream
                                // depends on the case — the reconciler deliberately never
                                // compares descriptions, because ~10% of prod fuel lines
                                // carry hand-typed ones no derivation reproduces.
                                onChange={(e) => updateLineItem(index, { description: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-1 text-right tabular-nums">
                              {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {isPctRateType(r.rateType) && <span className="ml-0.5 text-[0.625rem] text-slate-400">%</span>}
                            </td>
                            <td className="py-1 text-center">
                              <button
                                type="button" onClick={() => removeLineItem(index)} disabled={ro}
                                aria-label="Remove line item"
                                className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:text-gray-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {error && (
                <p className="mt-2 text-[0.75rem] font-semibold text-red-600 dark:text-red-400">{error}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-2.5 dark:border-gray-700">
          {/* Destructive, so it sits apart from the Cancel/Save pair rather than
              beside them. Hidden once invoiced — V1 stops rendering the link at
              that point (lib_editrecord.pl:18993) and the route refuses it too,
              with no Admin Override arm. */}
          {isEdit && !invoiced && (
            <AccessorialDeleteGate>
              <button
                type="button" onClick={() => setDeleteOpen(true)} disabled={saving}
                className="mr-auto rounded bg-brand-red px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete Accessorial
              </button>
            </AccessorialDeleteGate>
          )}
          <button
            type="button" onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button" onClick={handleSave} disabled={saving}
            className="rounded bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {deleteOpen && isEdit && accessorialNum != null && (
        <DeleteAccessorialDialog
          pronumber={pronumber}
          recno={recno!}
          accessorialNum={accessorialNum}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => {
            // The row is gone, so there is nothing left to edit behind this —
            // refresh the list and close the editor rather than leaving a stale
            // form open on a deleted accessorial.
            setDeleteOpen(false);
            onSaved();
            onClose();
          }}
        />
      )}
    </div>,
    document.body,
  );
};

export default AccessorialEditModal;
